import { Formik, FormikHelpers, useFormikContext } from "formik";
import {
  ComputedField,
  Form,
  FormRow,
  FormGroup,
  WrapGroup,
} from "./PickAndPlaceStyles";
import FormikNumberInput from "../../formik-form-components/FormikNumberInput";
import Button from "../../common/Button";
import { ButtonTypes } from "../../common/Button/constants";
import React, { useEffect, useMemo } from "react";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import {
  BillOfMaterials,
  Board,
  BOM_PROCESSING_STATES,
  Dimensions,
  ElementPlacement,
  Fiducial,
  ManufacturingDetails,
  PhysicalDetailsState,
} from "../../../reducers/uploadedBoard";
import * as Yup from "yup";
import { GentleText } from "../ManufacturersPanelStyles";
import PanelRendering, { XYCoordinate } from "./PanelRendering";
import {
  FlashPCBTable,
  InputLabel,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../constants/Styles";
import ViewAndDownloadPlacements from "./ViewAndDownloadPlacements";
import LoadingOverlay from "../../LoadingOverlay";
import LoadingIcon from "../../common/LoadingIcon";
import { useBillOfMaterials } from "../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import { useUpdatePickAndPlaceData } from "../../../utils/hooks/commonly-stored/uploaded-board/updates/board";

const schema = Yup.object().shape({
  pick_and_place: Yup.object().shape({
    left_rail_width: Yup.number().required().min(0),
    right_rail_width: Yup.number().required().min(0),
    top_rail_width: Yup.number().required().min(0),
    bottom_rail_width: Yup.number().required().min(0),
    num_rows: Yup.number().required().min(1),
    row_spacing: Yup.number().required().min(0),
    num_columns: Yup.number().required().min(1),
    column_spacing: Yup.number().required().min(0),
    x_offset: Yup.number().required(),
    y_offset: Yup.number().required(),
    panel_width: Yup.number().required(),
    panel_height: Yup.number().required(),
    unit: Yup.string().required(),
    rotation_degrees: Yup.number().required(),
  }),
});

function rotate(
  origin: XYCoordinate,
  pointToRotate: XYCoordinate,
  angle: number,
): XYCoordinate {
  const radians = (Math.PI / 180) * angle;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const x =
    cos * (pointToRotate.x - origin.x) -
    sin * (pointToRotate.y - origin.y) +
    origin.x;
  const y =
    cos * (pointToRotate.y - origin.y) +
    sin * (pointToRotate.x - origin.x) +
    origin.y;
  return { x, y };
}

function translate(point: XYCoordinate, offset: XYCoordinate): XYCoordinate {
  return {
    x: point.x + offset.x,
    y: point.y + offset.y,
  };
}

function transformPNPData(
  point: XYCoordinate,
  rotationDegrees: number,
  offset?: XYCoordinate,
) {
  return translate(
    rotate({ x: 0, y: 0 }, point, rotationDegrees),
    offset || { x: 0, y: 0 },
  );
}

function bomToPNPData(
  billOfMaterials: BillOfMaterials,
  rotationDegrees: number,
  offset: XYCoordinate,
): XYCoordinate[] {
  // Note that this function does not care about the rotations of the placements
  // because the viewer this is used in does not render the rotation. A dot is used
  // to represent the placement
  return billOfMaterials.elements
    .filter((element) => {
      return !element.do_not_populate && element.placement !== null;
    })
    .map((element) => {
      const placement = element.placement as ElementPlacement;
      return transformPNPData(
        { x: placement.x, y: placement.y },
        rotationDegrees,
        offset,
      );
    });
}

function getRotatedDimensions(
  dimensions: Dimensions,
  rotationDegrees: number,
): Dimensions {
  const points = [
    { x: dimensions.x_min, y: dimensions.y_min },
    { x: dimensions.x_min, y: dimensions.y_max },
    { x: dimensions.x_max, y: dimensions.y_max },
    { x: dimensions.x_max, y: dimensions.y_min },
  ];
  const rotatedBoard = points.map((point) => {
    return transformPNPData(point, rotationDegrees);
  });
  const xValues = rotatedBoard.map(({ x }) => x);
  const yValues = rotatedBoard.map(({ y }) => y);
  return {
    x_min: Math.min(...xValues),
    x_max: Math.max(...xValues),
    y_min: Math.min(...yValues),
    y_max: Math.max(...yValues),
    unit: dimensions.unit,
  };
}

function SmartViewer({
  billOfMaterials,
  dimensions,
}: {
  billOfMaterials: BillOfMaterials;
  dimensions: Dimensions;
}) {
  const {
    setFieldValue,
    values: {
      pick_and_place: {
        rotation_degrees,
        right_rail_width,
        left_rail_width,
        top_rail_width,
        bottom_rail_width,
        num_rows,
        row_spacing,
        num_columns,
        column_spacing,
        fiducials: {
          x_offset: fiducials_x_offset,
          top_right_x_offset: fiducials_top_right_x_offset,
          y_offset: fiducials_y_offset,
        },
      },
    },
  } = useFormikContext<ManufacturingDetails>();

  const rotatedDimensions = getRotatedDimensions(dimensions, rotation_degrees);
  const boardWidth = rotatedDimensions.x_max - rotatedDimensions.x_min;
  const boardHeight = rotatedDimensions.y_max - rotatedDimensions.y_min;

  const x_offset = -rotatedDimensions.x_max - right_rail_width;
  const y_offset = -rotatedDimensions.y_min + bottom_rail_width;
  const panel_width =
    left_rail_width +
    boardWidth * num_columns +
    column_spacing * (num_columns - 1) +
    right_rail_width;
  const panel_height =
    bottom_rail_width +
    boardHeight * num_rows +
    row_spacing * (num_rows - 1) +
    top_rail_width;
  const pitch_width = boardWidth + column_spacing;
  const pitch_height = boardHeight + row_spacing;
  const fiducialTopLocations = useMemo(() => {
    return [
      // Bottom Left
      { x: fiducials_x_offset - panel_width, y: fiducials_y_offset },
      // Bottom Right
      { x: -fiducials_x_offset, y: fiducials_y_offset },
      // Top Right
      {
        x: -fiducials_top_right_x_offset,
        y: panel_height - fiducials_y_offset,
      },
      // Top Left
      {
        x: fiducials_x_offset - panel_width,
        y: panel_height - fiducials_y_offset,
      },
    ];
  }, [
    fiducials_top_right_x_offset,
    fiducials_x_offset,
    fiducials_y_offset,
    panel_height,
    panel_width,
  ]);

  useEffect(() => {
    void setFieldValue("pick_and_place.x_offset", x_offset);
    void setFieldValue("pick_and_place.y_offset", y_offset);
    void setFieldValue("pick_and_place.panel_width", panel_width);
    void setFieldValue("pick_and_place.panel_height", panel_height);
    void setFieldValue("pick_and_place.pitch_width", pitch_width);
    void setFieldValue("pick_and_place.pitch_height", pitch_height);
    void setFieldValue(
      "pick_and_place.fiducials.top_locations",
      fiducialTopLocations,
    );
    void setFieldValue(
      "pick_and_place.fiducials.bottom_locations",
      fiducialTopLocations.map((fiducial) => {
        return {
          x: -fiducial.x - panel_width,
          y: fiducial.y,
        };
      }),
    );
  }, [
    panel_height,
    panel_width,
    setFieldValue,
    x_offset,
    y_offset,
    pitch_width,
    pitch_height,
    fiducialTopLocations,
  ]);

  const boards = Array.from({ length: num_rows }).flatMap((_, rowIndex) => {
    const matrixOffsetY = rowIndex * (boardHeight + row_spacing);
    return Array.from({ length: num_columns }).map((_, columnIndex) => {
      const matrixOffsetX = columnIndex * (boardWidth + column_spacing);
      return {
        minX: rotatedDimensions.x_min + x_offset - matrixOffsetX,
        minY: rotatedDimensions.y_min + y_offset + matrixOffsetY,
        width: boardWidth,
        height: boardHeight,
      };
    });
  });

  return (
    <PanelRendering
      panel={{
        minX: -panel_width,
        minY: 0,
        width: panel_width,
        height: panel_height,
      }}
      boards={boards}
      elementCoords={bomToPNPData(billOfMaterials, rotation_degrees, {
        x: x_offset,
        y: y_offset,
      })}
      fiducialLocations={fiducialTopLocations}
    />
  );
}

function FiducialsTable({
  title,
  fiducials,
}: {
  title: string;
  fiducials: Fiducial[];
}) {
  return (
    <div>
      <InputLabel>{title}</InputLabel>
      <FlashPCBTable width={"20rem"}>
        <thead>
          <TableHeaderRow>
            <TableHeaderCell>x</TableHeaderCell>
            <TableHeaderCell>y</TableHeaderCell>
          </TableHeaderRow>
        </thead>
        <tbody>
          {fiducials.map((location) => {
            return (
              <TableRow key={JSON.stringify(location)}>
                <TableCell>
                  {location.x.toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                  })}
                </TableCell>
                <TableCell>
                  {location.y.toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                  })}
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </FlashPCBTable>
    </div>
  );
}

export default function TransformationsForm() {
  const boardStuff = useBoardFromContext();
  const board = boardStuff.board as Board;
  const billOfMaterials = useBillOfMaterials();
  const { updatePickAndPlaceData, updatingPickAndPlaceData } =
    useUpdatePickAndPlaceData();

  const saveTransformations = (
    values: ManufacturingDetails,
    { resetForm }: FormikHelpers<ManufacturingDetails>,
  ) => {
    updatePickAndPlaceData(values);
    resetForm({ values });
  };

  if (
    board.physical_details_state != PhysicalDetailsState.PROCESSED ||
    billOfMaterials === null ||
    board.manufacturing_details === null
  ) {
    return <LoadingIcon text={"Processing physical board details..."} />;
  }

  const dimensions = board.physical_details?.dimensions;

  if (!dimensions) {
    return (
      <>
        <GentleText>
          This board needs to be reprocessed. Please contact a FlashPCB software
          engineer for assistance.
        </GentleText>
      </>
    );
  }

  if (BOM_PROCESSING_STATES.has(billOfMaterials.state)) {
    return (
      <LoadingIcon
        text={"Processing BOM..."}
        centerHorizontally
        centerVertically
      />
    );
  }

  return (
    <>
      <LoadingOverlay
        loading={updatingPickAndPlaceData}
        text={"Saving transformation..."}
      />
      <Formik
        initialValues={board.manufacturing_details}
        onSubmit={saveTransformations}
        validationSchema={schema}
      >
        {({ handleSubmit, values, dirty }) => {
          const panelDetailsAreReady =
            values.pick_and_place.panel_width &&
            values.pick_and_place.panel_height &&
            values.pick_and_place.pitch_width &&
            values.pick_and_place.pitch_height;
          return (
            <Form onSubmit={handleSubmit}>
              <WrapGroup>
                <FormGroup>
                  <FormRow>
                    <FormikNumberInput
                      name="pick_and_place.num_rows"
                      label={"Number of rows"}
                      min={1}
                    />
                    <FormikNumberInput
                      name="pick_and_place.row_spacing"
                      label={"Spacing between rows"}
                      min={0}
                    />
                    <FormikNumberInput
                      name="pick_and_place.num_columns"
                      label={"Number of columns"}
                      min={1}
                    />
                    <FormikNumberInput
                      name="pick_and_place.column_spacing"
                      label={"Spacing between columns"}
                      min={0}
                    />
                  </FormRow>
                  <FormRow>
                    <FormikNumberInput
                      name="pick_and_place.left_rail_width"
                      label={"Left rail width"}
                      min={0}
                    />
                    <FormikNumberInput
                      name="pick_and_place.right_rail_width"
                      label={"Right rail width"}
                      min={0}
                    />
                    <FormikNumberInput
                      name="pick_and_place.top_rail_width"
                      label={"Top rail width"}
                      min={0}
                    />
                    <FormikNumberInput
                      name="pick_and_place.bottom_rail_width"
                      label={"Bottom rail width"}
                      min={0}
                    />
                  </FormRow>
                  <FormRow>
                    <FormikNumberInput
                      name="pick_and_place.rotation_degrees"
                      label={`Rotation degrees`}
                    />
                  </FormRow>
                </FormGroup>
                <FormGroup>
                  <FormRow>
                    <div>
                      <InputLabel>
                        x Offset {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.x_offset.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                    <div>
                      <InputLabel>
                        y Offset {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.y_offset.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                  </FormRow>
                  <FormRow>
                    <div>
                      <InputLabel>
                        Panel width {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.panel_width.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                    <div>
                      <InputLabel>
                        Panel height {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.panel_height.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                  </FormRow>
                  <FormRow>
                    <div>
                      <InputLabel>
                        Pitch width {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.pitch_width.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                    <div>
                      <InputLabel>
                        Pitch height {values.pick_and_place.unit}
                      </InputLabel>
                      <ComputedField>
                        {values.pick_and_place.pitch_height.toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 3,
                          },
                        )}
                      </ComputedField>
                    </div>
                  </FormRow>
                </FormGroup>
              </WrapGroup>
              <SmartViewer
                billOfMaterials={billOfMaterials}
                dimensions={dimensions}
              />
              <WrapGroup>
                <FormGroup>
                  <FormikNumberInput
                    name="pick_and_place.fiducials.x_offset"
                    label={`x Offset`}
                  />
                  <FormikNumberInput
                    name="pick_and_place.fiducials.top_right_x_offset"
                    label={`Top right x offset`}
                  />
                  <FormikNumberInput
                    name="pick_and_place.fiducials.y_offset"
                    label={`y Offset`}
                  />
                </FormGroup>
                <FiducialsTable
                  title={"Top Fiducials"}
                  fiducials={values.pick_and_place.fiducials.top_locations}
                />
                <FiducialsTable
                  title={"Bottom Fiducials"}
                  fiducials={values.pick_and_place.fiducials.bottom_locations}
                />
                <FiducialsTable
                  title={"Fiducials with origin bottom left"}
                  fiducials={values.pick_and_place.fiducials.top_locations.map(
                    (location) => {
                      return {
                        x: location.x + values.pick_and_place.panel_width,
                        y: location.y,
                      };
                    },
                  )}
                />
              </WrapGroup>
              <div>
                <Button
                  label="Confirm transformations"
                  type={ButtonTypes.SUBMIT}
                  centerText
                />
              </div>
              {dirty || !panelDetailsAreReady ? (
                <GentleText>
                  Confirm panel details to download the PnP files
                </GentleText>
              ) : (
                <ViewAndDownloadPlacements />
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
